import { useCallback, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, Video as VideoIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { BlogPost } from '@/types/blog';
import { JsonLd } from '@/components/seo/JsonLd';
import { SITE_ORIGIN } from '@/lib/seo/site-url';
import { OpenGraph } from '@/components/seo/OpenGraph';
import { Helmet } from 'react-helmet-async';
import { useIframePandaPlayer } from '@/hooks/useIframePandaPlayer';
import { useVideoSync } from '@/hooks/useVideoSync';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface BlogPostWithVideoProps {
  post: BlogPost;
}

/**
 * BlogPostWithVideo - Renderização de post com vídeo sincronizado
 *
 * Features:
 * - Desktop: Layout side-by-side com vídeo sticky
 * - Mobile: Layout 50/50 split (vídeo top, artigo bottom)
 * - Sincronização bidirecional vídeo ↔ artigo
 * - Table of contents interativo
 * - Auto-scroll baseado no vídeo
 */
export function BlogPostWithVideo({ post }: BlogPostWithVideoProps) {
  const [autoScroll, setAutoScroll] = useState(true);
  const isMobile = useIsMobile();

  // Validar configuração de vídeo
  if (!post.video || !post.videoSections || post.videoSections.length === 0) {
    console.error('Post configurado como vídeo mas sem dados de vídeo válidos');
    return null;
  }

  // Construir URL do embed
  const embedUrl =
    post.video.embedUrl ||
    (post.video.videoId && post.video.libraryId
      ? `https://player-vz-${post.video.libraryId}.tv.pandavideo.com.br/embed/?v=${post.video.videoId}&api=1`
      : null);

  if (!embedUrl) {
    console.error('Nenhuma URL de vídeo disponível para o post');
    return null;
  }

  // Integração com Panda Player via iframe (hook original que funciona)
  const { state: playerState, controls, iframeRef } = useIframePandaPlayer({
    embedUrl,
    onReady: () => {
      console.log('✅ Player pronto!');
    },
  });

  // Converter videoSections para formato esperado pelo useVideoSync
  const sectionsForSync = post.videoSections.map((section) => ({
    id: section.id,
    title: section.title,
    startTime: section.startTime,
    endTime: section.endTime,
    content: '', // Não usado para sincronização
  }));

  // Sincronização bidirecional (hook original que funciona)
  const { activeSection, navigateToSection } = useVideoSync({
    sections: sectionsForSync,
    currentTime: playerState.currentTime,
    autoScroll,
  });

  /**
   * Handler para navegação por clique no índice
   * Pula o vídeo para o timestamp correto e faz scroll no artigo
   */
  const handleSectionClick = useCallback(
    (sectionId: string) => {
      const timestamp = navigateToSection(sectionId);
      if (timestamp !== null) {
        console.log('🎯 Pulando vídeo para:', timestamp, 's');
        controls.seekTo(timestamp);
        // Pausa auto-scroll temporariamente quando usuário navega manualmente
        setAutoScroll(false);
        setTimeout(() => setAutoScroll(true), 3000);
      }
    },
    [navigateToSection, controls]
  );

  // Preparar dados para SEO
  const baseUrl = SITE_ORIGIN;
  const fullUrl = `${baseUrl}${post.url}`;
  const imageUrl = post.coverImage.startsWith('http')
    ? post.coverImage
    : `${baseUrl}${post.coverImage}`;

  // Scroll to top on mount (desktop only)
  useEffect(() => {
    if (!isMobile) {
      window.scrollTo(0, 0);
    }
  }, [isMobile]);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.seo?.title || post.title} | Johnny Vaz</title>
        <meta name="description" content={post.seo?.description || post.excerpt} />
        {post.seo?.keywords && (
          <meta name="keywords" content={post.seo.keywords.join(', ')} />
        )}
        <link rel="canonical" href={fullUrl} />
      </Helmet>

      {/* JSON-LD Schema - TODO: Estender com VideoObject */}
      <JsonLd post={post} baseUrl={baseUrl} />

      {/* Open Graph */}
      <OpenGraph
        title={post.seo?.title || post.title}
        description={post.seo?.description || post.excerpt}
        url={fullUrl}
        image={imageUrl}
        type="article"
        siteName="Johnny Vaz"
        locale={post.agentMetadata?.lang?.replace('-', '_') || 'pt_BR'}
        publishedTime={post.date.toISOString()}
        modifiedTime={post.lastModified?.toISOString()}
        author={post.schema?.author || 'Johnny Vaz'}
        section={post.categories[0]}
        tags={post.tags}
      />

      <div className="container mx-auto px-4 py-8">
        {/* Botão de voltar */}
        <Button variant="ghost" size="sm" asChild className="mb-6">
          <Link to="/blog" className="flex items-center group">
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            Voltar para o blog
          </Link>
        </Button>

        {/* Cabeçalho do artigo */}
        <header className="mb-8">
          <div className="flex items-start gap-2 mb-4">
            <Badge variant="secondary" className="bg-primary/10 text-primary">
              <VideoIcon className="h-3 w-3 mr-1" />
              Com vídeo
            </Badge>
            {post.categories.map((category, i) => (
              <Badge key={i} variant="secondary">
                <Tag size={12} className="mr-1" /> {category}
              </Badge>
            ))}
          </div>

          <h1 className="mb-4 text-4xl font-bold tracking-tight text-foreground lg:text-5xl">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="mb-6 text-xl text-muted-foreground">{post.excerpt}</p>
          )}

          {/* Metadados */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {post.schema?.author && (
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{post.schema.author}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <time dateTime={post.date.toISOString()}>
                {new Date(post.date).toLocaleDateString('pt-BR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </time>
            </div>
          </div>
        </header>

        {/* Layout principal: Vídeo + Artigo */}
        <div className="grid gap-8 lg:grid-cols-12">
          {/* Coluna do vídeo (sticky no desktop) */}
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="lg:sticky lg:top-20">
              {/* Vídeo */}
              <div className="relative mb-4 w-full overflow-hidden rounded-lg bg-black shadow-lg">
                <iframe
                  ref={iframeRef}
                  src={embedUrl}
                  className="aspect-video w-full"
                  style={{ border: 'none' }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                  allowFullScreen
                  title="Panda Video Player"
                />
              </div>

              {/* Controles e informações */}
              <div className="mb-4 rounded-lg bg-muted p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Tempo: </span>
                    <span className="font-mono font-medium">
                      {Math.floor(playerState.currentTime / 60)}:
                      {String(Math.floor(playerState.currentTime % 60)).padStart(2, '0')}
                      {playerState.duration > 0 && (
                        <>
                          {' / '}
                          {Math.floor(playerState.duration / 60)}:
                          {String(Math.floor(playerState.duration % 60)).padStart(2, '0')}
                        </>
                      )}
                    </span>
                    <span className="ml-2 text-xs">
                      {playerState.isPlaying ? '▶️ Reproduzindo' : '⏸️ Pausado'}
                    </span>
                  </div>

                  <button
                    onClick={() => setAutoScroll(!autoScroll)}
                    className={cn(
                      'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                      autoScroll
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-background text-foreground hover:bg-muted'
                    )}
                  >
                    {autoScroll ? '🔄 Auto-scroll ON' : '⏸️ Auto-scroll OFF'}
                  </button>
                </div>

                <p className="text-xs text-muted-foreground">
                  Clique nas seções para navegar • O artigo rola automaticamente com o vídeo
                </p>
              </div>

              {/* Table of Contents */}
              <div className="rounded-lg border bg-card p-4">
                <h3 className="mb-3 text-sm font-semibold">Índice do Vídeo</h3>
                <nav className="space-y-1">
                  {post.videoSections.map((section) => {
                    const timestamp = `${Math.floor(section.startTime / 60)}:${String(Math.floor(section.startTime % 60)).padStart(2, '0')}`;
                    return (
                      <button
                        key={section.id}
                        onClick={() => handleSectionClick(section.id)}
                        className={cn(
                          'w-full text-left rounded-md px-3 py-2 text-sm transition-colors',
                          activeSection === section.id
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-muted'
                        )}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{section.title}</span>
                          <span className="text-xs opacity-70 mt-0.5">
                            ⏱️ {timestamp}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </div>

          {/* Coluna do artigo (scrollável) */}
          <div className="lg:col-span-7 xl:col-span-8">
            <article
              className="prose dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: post.html }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
